import { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Stack,
  Paper,
  Container,
  Typography,
  IconButton,
  Box,
  Divider,
  TextField,
  Modal,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form, FormikProvider, useFormik } from 'formik';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import Iconify from '../../../components/iconify';
import { INITIAL_WFH_FORM_STATE, WFH_HALF_DAY_TYPE_LABELS, WFH_HALF_DAY_TYPES } from '../constants';
import WfhRequestSchema from '../schema/wfhSchema';
import { getReviewers } from '../../../services/wfhService';

const WfhRequestForm = ({ open, onClose, onSubmit, wfhData = null, isEdit = false }) => {
  const { data: reviewersResponse } = useQuery({
    queryKey: ['wfhReviewers'],
    queryFn: async () => {
      try {
        const response = await getReviewers();
        return {
          reviewers: response?.data?.data || [],
          minStartDate: response?.data?.minStartDate || null,
        };
      } catch (error) {
        console.error('Error fetching WFH reviewers:', error);
        return { reviewers: [], minStartDate: null };
      }
    },
    enabled: open,
  });

  const reviewersList = reviewersResponse?.reviewers || [];

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      const submitData = {
        ...values,
        startDate: values.startDate ? dayjs(values.startDate).format('YYYY-MM-DD') : null,
        endDate:
          values.isHalfDay && values.startDate
            ? dayjs(values.startDate).format('YYYY-MM-DD')
            : values.endDate
            ? dayjs(values.endDate).format('YYYY-MM-DD')
            : null,
        halfDayType: values.isHalfDay ? values.halfDayType : null,
        reviewers: values.reviewers && values.reviewers.length > 0 ? values.reviewers : undefined,
      };
      await onSubmit(submitData);
      if (!isEdit) {
        resetForm({ values: INITIAL_WFH_FORM_STATE });
      }
    } catch (error) {
      // Error handled by mutation's onError; form data preserved for correction
    } finally {
      setSubmitting(false);
    }
  };

  const getInitialValues = () => {
    if (isEdit && wfhData) {
      return {
        startDate: wfhData.startDate ? dayjs(wfhData.startDate) : null,
        endDate: wfhData.endDate ? dayjs(wfhData.endDate) : null,
        isHalfDay: wfhData.isHalfDay || false,
        halfDayType: wfhData.halfDayType || '',
        reason: wfhData.reason || '',
        reviewers: wfhData.reviewers || [],
      };
    }
    return INITIAL_WFH_FORM_STATE;
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: WfhRequestSchema(),
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const { values, errors, touched, isSubmitting, submitForm, getFieldProps, setFieldValue, resetForm } = formik;

  const startDateMin = reviewersResponse?.minStartDate
    ? dayjs(reviewersResponse.minStartDate).startOf('day')
    : dayjs().subtract(1, 'month').startOf('day');

  const handleHalfDayToggle = (checked) => {
    setFieldValue('isHalfDay', checked);
    if (checked && values.startDate) {
      setFieldValue('endDate', values.startDate);
      if (!values.halfDayType) {
        setFieldValue('halfDayType', WFH_HALF_DAY_TYPES.FIRST_HALF);
      }
    } else {
      setFieldValue('halfDayType', '');
    }
  };

  useEffect(() => {
    if (values.isHalfDay && values.startDate) {
      setFieldValue('endDate', values.startDate);
    }
  }, [values.startDate, values.isHalfDay, setFieldValue]);

  const handleClose = () => {
    onClose();
    resetForm({ values: INITIAL_WFH_FORM_STATE });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 600,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {isEdit ? 'Edit WFH Request' : 'Request WFH'}
            </Typography>
            <IconButton color="primary" onClick={handleClose}>
              <Iconify icon="ic:round-clear" />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Container maxWidth="sm">
            <FormikProvider value={formik}>
              <Form onSubmit={formik.handleSubmit}>
                <Stack spacing={3}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Start Date"
                      value={values.startDate ? dayjs(values.startDate) : null}
                      onChange={(newValue) => {
                        setFieldValue('startDate', newValue);
                        if (values.endDate && newValue && dayjs(values.endDate).isBefore(newValue)) {
                          setFieldValue('endDate', newValue);
                        }
                      }}
                      inputFormat="DD-MMM-YYYY"
                      minDate={startDateMin}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={touched.startDate && Boolean(errors.startDate)}
                          helperText={touched.startDate && errors.startDate}
                          fullWidth
                        />
                      )}
                    />
                  </LocalizationProvider>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label={values.isHalfDay ? 'Date (Half Day)' : 'End Date'}
                      value={values.endDate ? dayjs(values.endDate) : null}
                      onChange={(newValue) => {
                        if (values.isHalfDay && values.startDate) {
                          setFieldValue('endDate', values.startDate);
                        } else {
                          setFieldValue('endDate', newValue);
                        }
                      }}
                      inputFormat="DD-MMM-YYYY"
                      minDate={values.startDate ? dayjs(values.startDate).startOf('day') : startDateMin}
                      disabled={values.isHalfDay}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={touched.endDate && Boolean(errors.endDate)}
                          helperText={
                            touched.endDate
                              ? errors.endDate
                              : values.isHalfDay
                              ? 'End date matches start date for half-day WFH'
                              : ''
                          }
                          fullWidth
                        />
                      )}
                    />
                  </LocalizationProvider>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.isHalfDay}
                        onChange={(e) => handleHalfDayToggle(e.target.checked)}
                      />
                    }
                    label="Half Day WFH"
                  />

                  {values.isHalfDay && (
                    <FormControl fullWidth error={touched.halfDayType && Boolean(errors.halfDayType)}>
                      <InputLabel>Half Day Type</InputLabel>
                      <Select
                        {...getFieldProps('halfDayType')}
                        label="Half Day Type"
                        value={values.halfDayType}
                        onChange={(e) => setFieldValue('halfDayType', e.target.value)}
                      >
                        <MenuItem value={WFH_HALF_DAY_TYPES.FIRST_HALF}>
                          {WFH_HALF_DAY_TYPE_LABELS[WFH_HALF_DAY_TYPES.FIRST_HALF]}
                        </MenuItem>
                        <MenuItem value={WFH_HALF_DAY_TYPES.SECOND_HALF}>
                          {WFH_HALF_DAY_TYPE_LABELS[WFH_HALF_DAY_TYPES.SECOND_HALF]}
                        </MenuItem>
                      </Select>
                      {touched.halfDayType && errors.halfDayType && (
                        <FormHelperText>{errors.halfDayType}</FormHelperText>
                      )}
                    </FormControl>
                  )}

                  <TextField
                    label="Reason"
                    {...getFieldProps('reason')}
                    inputProps={{ maxLength: 500 }}
                    error={touched.reason && Boolean(errors.reason)}
                    helperText={touched.reason ? errors.reason : `${values.reason?.length || 0}/500 characters`}
                    multiline
                    rows={4}
                    fullWidth
                    required
                  />

                  <FormControl fullWidth error={touched.reviewers && Boolean(errors.reviewers)} required>
                    <InputLabel>Select Reviewers (Managers/HR) *</InputLabel>
                    <Select
                      multiple
                      value={values.reviewers || []}
                      onChange={(e) => {
                        const { value } = e.target;
                        setFieldValue('reviewers', typeof value === 'string' ? value.split(',') : value);
                      }}
                      renderValue={(selected) => {
                        if (!selected || selected.length === 0) return '';
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((reviewerId) => {
                              const reviewer = reviewersList.find((r) => r.id === reviewerId);
                              return (
                                <Chip
                                  key={reviewerId}
                                  label={reviewer ? reviewer.userName : reviewerId}
                                  size="small"
                                />
                              );
                            })}
                          </Box>
                        );
                      }}
                      label="Select Reviewers (Managers/HR) *"
                    >
                      {reviewersList.map((reviewer) => (
                        <MenuItem key={reviewer.id} value={reviewer.id}>
                          {reviewer.userName} (
                          {reviewer.userRoleId === 2 ? 'Manager' : reviewer.userRoleId === 6 ? 'HR' : 'Admin'})
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.reviewers && errors.reviewers && (
                      <FormHelperText>{errors.reviewers}</FormHelperText>
                    )}
                    {!errors.reviewers && (
                      <FormHelperText>
                        Select at least one reviewer. Admin and HR will automatically be included.
                      </FormHelperText>
                    )}
                  </FormControl>

                  <LoadingButton
                    fullWidth
                    size="large"
                    onClick={submitForm}
                    variant="contained"
                    loading={isSubmitting}
                  >
                    {isEdit ? 'Update Request' : 'Submit Request'}
                  </LoadingButton>
                </Stack>
              </Form>
            </FormikProvider>
          </Container>
        </Paper>
      </Box>
    </Modal>
  );
};

WfhRequestForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  wfhData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default WfhRequestForm;
